import { useEffect, useRef, useState } from "react";

// Maximum range of time the slider exposes, anchored to "now". 6 hours covers
// a full service-day's worth of recent activity without making the slider so
// coarse that single 10-second snapshots become invisible. The actual reach
// is further clamped to how much history the backend has on hand, so the
// handle can't drag past the start of our data.
const WINDOW_SEC = 6 * 60 * 60;

// Playback speed (sim seconds advanced per real second) and tick cadence used
// by the Play button. 60x means 1 minute of history replays in 1 real second,
// which makes a multi-hour scrub watchable without feeling sluggish.
const PLAY_SPEED = 60;
const PLAY_TICK_MS = 250;

// How often to re-poll the range endpoint. The available history grows as
// snapshots accumulate, so we refresh periodically to extend the slider.
const RANGE_REFRESH_MS = 60_000;

// Floor for the slider's reach so dragging is meaningful even when the
// backend only has a handful of seconds of data (e.g. just after startup).
const MIN_REACH_SEC = 60;

type Props = {
  /** Currently-displayed timestamp (unix seconds); null when in live mode. */
  value: number | null;
  /** Called with the new offset in seconds (0 = now, positive = N seconds ago). */
  onScrub: (offsetSec: number) => void;
  /** Snap back to the live SSE stream. */
  onLive: () => void;
};

// TimeSlider is the overlay control on the Map view that lets the user
// scrub backward in time. When the user is at the right edge it shows "live"
// and the parent uses the SSE stream; anywhere else, the parent fetches a
// reconstructed snapshot from /api/realtime/vehicles/at.
export function TimeSlider({ value, onScrub, onLive }: Props) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [playing, setPlaying] = useState(false);
  // oldestTs is the unix-seconds timestamp of the earliest sample the
  // backend still has on hand. null = unknown (haven't fetched yet, or the
  // endpoint failed); 0 = backend reports no data.
  const [oldestTs, setOldestTs] = useState<number | null>(null);

  // Keep "now" fresh so the relative-time label ("3m ago") and the live
  // edge of the slider don't drift while the user holds the handle still.
  useEffect(() => {
    const id = window.setInterval(
      () => setNow(Math.floor(Date.now() / 1000)),
      1000,
    );
    return () => window.clearInterval(id);
  }, []);

  // Poll the range endpoint so the slider's left edge tracks how much
  // history is actually available. Fetched up-front and then refreshed
  // periodically as more snapshots accumulate.
  useEffect(() => {
    let cancelled = false;
    const fetchRange = async () => {
      try {
        const res = await fetch("/api/realtime/vehicles/range");
        if (!res.ok) return;
        const data = (await res.json()) as { oldest?: number };
        if (cancelled) return;
        setOldestTs(typeof data.oldest === "number" ? data.oldest : 0);
      } catch {
        // Network blip — keep whatever range we last knew about and try
        // again on the next tick.
      }
    };
    fetchRange();
    const id = window.setInterval(fetchRange, RANGE_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const live = value === null;
  const ts = value ?? now;

  // Reach is how far back the slider can go: capped by the global window,
  // floored so the control isn't a degenerate zero-width track, and (once
  // we know it) clamped to the actual data we have on hand.
  let reach = WINDOW_SEC;
  if (oldestTs !== null && oldestTs > 0) {
    reach = Math.min(WINDOW_SEC, Math.max(MIN_REACH_SEC, now - oldestTs));
  } else if (oldestTs === 0) {
    // Backend has no data at all — leave the minimum reach so the control
    // still renders, but it'll effectively be a no-op.
    reach = MIN_REACH_SEC;
  }

  // Slider is rendered as "seconds ago" (0..reach) so the rightmost
  // position is "live" regardless of when `now` updates.
  const offset = Math.min(reach, Math.max(0, now - ts));
  // Left = oldest, right = live; invert so the visual edge matches.
  const sliderValue = reach - offset;

  // Going live cancels playback implicitly — there's nothing to "replay"
  // when we're already at the live edge.
  useEffect(() => {
    if (live && playing) setPlaying(false);
  }, [live, playing]);

  // Playback loop: advance the historical timestamp forward at PLAY_SPEED.
  // We compute the next ts off the latest props each tick (rather than off
  // a captured value) so the loop survives manual scrubs mid-playback.
  const onScrubRef = useRef(onScrub);
  onScrubRef.current = onScrub;
  const onLiveRef = useRef(onLive);
  onLiveRef.current = onLive;
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!playing) return;
    const stepSec = (PLAY_SPEED * PLAY_TICK_MS) / 1000;
    const id = window.setInterval(() => {
      const cur = valueRef.current;
      if (cur === null) {
        setPlaying(false);
        return;
      }
      const nowSec = Math.floor(Date.now() / 1000);
      const nextTs = cur + stepSec;
      const nextOffset = nowSec - nextTs;
      if (nextOffset <= 0) {
        onLiveRef.current();
        setPlaying(false);
      } else {
        onScrubRef.current(nextOffset);
      }
    }, PLAY_TICK_MS);
    return () => window.clearInterval(id);
  }, [playing]);

  const handle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value);
    const newOffset = reach - v;
    if (newOffset <= 0) onLive();
    else onScrub(newOffset);
  };

  const togglePlay = () => {
    if (live) return;
    setPlaying((p) => !p);
  };

  return (
    <div className="time-slider" role="group" aria-label="Time travel">
      <button
        type="button"
        className={`time-play ${playing ? "active" : ""}`}
        onClick={togglePlay}
        disabled={live}
        title={
          live
            ? "Scrub back in time to enable playback"
            : playing
              ? "Pause playback"
              : "Play history forward"
        }
        aria-label={playing ? "Pause playback" : "Play history forward"}
        aria-pressed={playing}
      >
        {playing ? "❚❚" : "▶"}
      </button>
      <button
        type="button"
        className={`time-live ${live ? "active" : ""}`}
        onClick={onLive}
        title="Jump to live"
        aria-pressed={live}
      >
        ● live
      </button>
      <input
        type="range"
        min={0}
        max={reach}
        step={10}
        value={sliderValue}
        onChange={handle}
        aria-label="Scrub backward in time"
        className="time-range"
      />
      <span className="time-readout" aria-live="polite">
        {live ? "now" : formatOffset(offset, ts)}
      </span>
    </div>
  );
}

function formatOffset(offsetSec: number, ts: number): string {
  const rel = relativeAgo(offsetSec);
  const abs = formatClock(ts);
  return `${rel} · ${abs}`;
}

function relativeAgo(secs: number): string {
  if (secs < 60) return `${secs}s ago`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m < 60) return s ? `${m}m ${s}s ago` : `${m}m ago`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m ago` : `${h}h ago`;
}

function formatClock(ts: number): string {
  const d = new Date(ts * 1000);
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  const ss = d.getSeconds().toString().padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

// useDebouncedHistoryFetch fetches the historical snapshot whenever `ts`
// changes (and is not null), debounced so dragging the slider doesn't
// hammer the backend with a request per frame.
export function useDebouncedHistoryFetch(
  ts: number | null,
  onResult: (vehicles: unknown[], generated: number) => void,
) {
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    if (ts === null) return;
    const ctl = new AbortController();
    const id = window.setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/realtime/vehicles/at?ts=${encodeURIComponent(String(ts))}`,
          { signal: ctl.signal },
        );
        if (!res.ok) return;
        const data = (await res.json()) as {
          vehicles?: unknown[];
          generated?: number;
        };
        onResultRef.current(data.vehicles ?? [], data.generated ?? ts);
      } catch {
        // AbortError or network blip — next slider movement will retry.
      }
    }, 120);
    return () => {
      window.clearTimeout(id);
      ctl.abort();
    };
  }, [ts]);
}
